// Experiments v2: server-driven A/B tests and rollout flags (wcc4-backend
// /admin/experiments-v2, docs/EXPERIMENTS_V2.md). The v1 Experiments page is
// separate and unchanged.
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Space, Tabs, Tooltip, Typography, message } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '@/services/api';
import type { ExpV2Counts, ExpV2Layer, ExpV2Test, ExpV2UserList } from '@/types/experimentsV2';
import { apiErrorMessage } from '@/utils/apiError';
import { AutoRefreshToggle } from '@/components/AutoRefreshToggle';
import { useAutoRefresh } from '@/hooks/useAutoRefresh';
import { HistoryPanel } from './HistoryPanel';
import { LayersPanel } from './LayersPanel';
import { ListsPanel } from './ListsPanel';
import { TestsTable } from './TestsTable';
import { useExperimentsAccess } from './useExperimentsAccess';

/** Counts run a 10 s query each; the list asks for the first few live tests only. */
const MAX_COUNTS = 10;
const TABS = ['tests', 'layers', 'lists', 'history'] as const;
type Tab = (typeof TABS)[number];

const needsCounts = (t: ExpV2Test) =>
  t.kind === 'experiment' && (t.status === 'running' || t.status === 'paused');

export const ExperimentsV2Page: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab: Tab = (TABS as readonly string[]).includes(params.get('tab') ?? '')
    ? (params.get('tab') as Tab)
    : 'tests';
  const { canCreate, canEdit } = useExperimentsAccess();

  const [tests, setTests] = useState<ExpV2Test[]>([]);
  const [counts, setCounts] = useState<Record<string, ExpV2Counts | undefined>>({});
  const [layers, setLayers] = useState<ExpV2Layer[]>([]);
  const [lists, setLists] = useState<ExpV2UserList[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshMs, setRefreshMs] = useState(60_000);

  const loadCounts = useCallback(async (list: ExpV2Test[]) => {
    const live = list.filter(needsCounts).slice(0, MAX_COUNTS);
    const answers = await Promise.allSettled(live.map((t) => adminApi.experimentsV2.counts(t.key)));
    const next: Record<string, ExpV2Counts | undefined> = {};
    answers.forEach((a, i) => {
      // A failed count leaves that row as a dash; the table itself still loads.
      if (a.status === 'fulfilled') next[live[i].key] = a.value.data;
    });
    setCounts(next);
  }, []);

  const loadTests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await adminApi.experimentsV2.list();
      setTests(data);
      void loadCounts(data);
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not load the tests'));
    } finally {
      setLoading(false);
    }
  }, [loadCounts]);

  const loadLayers = useCallback(async () => {
    try {
      const { data } = await adminApi.experimentsV2.listLayers();
      setLayers(data);
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not load the layers'));
    }
  }, []);

  const loadLists = useCallback(async () => {
    try {
      const { data } = await adminApi.experimentsV2.listLists();
      setLists(data);
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not load the user lists'));
    }
  }, []);

  const reload = useCallback(() => {
    if (tab === 'tests') return loadTests();
    if (tab === 'layers') return loadLayers();
    if (tab === 'lists') return loadLists();
    return undefined;
  }, [tab, loadTests, loadLayers, loadLists]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useAutoRefresh(() => loadCounts(tests), autoRefresh && tab === 'tests', refreshMs);

  const createLayer = async (values: { key: string; name: string; description?: string }) => {
    try {
      await adminApi.experimentsV2.createLayer(values);
      message.success(`Layer ${values.key} created`);
      await loadLayers();
    } catch (e) {
      message.error(apiErrorMessage(e, 'Could not create the layer'));
      throw e;
    }
  };

  const setHoldout = async (buckets: number) => {
    try {
      await adminApi.experimentsV2.setHoldoutSize(buckets);
      message.success('Holdout size saved');
      await loadLayers();
    } catch (e) {
      message.error(apiErrorMessage(e, 'Could not change the holdout'));
      throw e;
    }
  };

  const createList = async (name: string, userIds: string[]) => {
    try {
      await adminApi.experimentsV2.createList({ name, userIds });
      message.success(`List ${name} created`);
      await loadLists();
    } catch (e) {
      message.error(apiErrorMessage(e, 'Could not create the list'));
      throw e;
    }
  };

  const updateList = async (id: string, body: { name?: string; userIds?: string[] }) => {
    try {
      await adminApi.experimentsV2.updateList(id, body);
      message.success('List saved');
      await loadLists();
    } catch (e) {
      message.error(apiErrorMessage(e, 'Could not save the list'));
      throw e;
    }
  };

  const loadList = async (id: string) => (await adminApi.experimentsV2.getList(id)).data;

  const newButton = (kind: 'experiment' | 'flag', label: string) => (
    <Tooltip title={canCreate ? undefined : 'Needs experiments:create'}>
      <Button
        icon={<PlusOutlined />}
        disabled={!canCreate}
        onClick={() => navigate(`/experiments-v2/new?kind=${kind}`)}
      >
        {label}
      </Button>
    </Tooltip>
  );

  return (
    <Card
      title={
        <Space direction="vertical" size={0}>
          <span>Experiments v2</span>
          <Typography.Text type="secondary" style={{ fontWeight: 'normal' }}>
            Server-driven A/B tests and rollout flags. Players see nothing until a test starts.
          </Typography.Text>
        </Space>
      }
      extra={
        <Space wrap>
          {newButton('experiment', 'New test')}
          {newButton('flag', 'New flag')}
          <Button
            icon={<ReloadOutlined />}
            disabled={tab === 'history'}
            onClick={() => void reload()}
          >
            Reload
          </Button>
        </Space>
      }
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <Tabs
        activeKey={tab}
        onChange={(key) => setParams({ tab: key })}
        items={[
          {
            key: 'tests',
            label: 'Tests',
            children: (
              <Space direction="vertical" style={{ width: '100%' }}>
                <AutoRefreshToggle
                  enabled={autoRefresh}
                  onEnabledChange={setAutoRefresh}
                  intervalMs={refreshMs}
                  onIntervalChange={setRefreshMs}
                />
                <TestsTable
                  tests={tests}
                  counts={counts}
                  loading={loading}
                  onOpen={(key) => navigate(`/experiments-v2/${key}`)}
                />
              </Space>
            ),
          },
          {
            key: 'layers',
            label: 'Layers',
            children: (
              <LayersPanel
                layers={layers}
                canCreate={canCreate}
                canEdit={canEdit}
                onCreate={createLayer}
                onHoldout={setHoldout}
              />
            ),
          },
          {
            key: 'lists',
            label: 'User lists',
            children: (
              <ListsPanel
                lists={lists}
                canCreate={canCreate}
                canEdit={canEdit}
                onCreate={createList}
                onUpdate={updateList}
                loadList={loadList}
              />
            ),
          },
          { key: 'history', label: 'Player history', children: <HistoryPanel /> },
        ]}
      />
    </Card>
  );
};
